import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { cn } from "../lib/cn";
import { buttonVariants } from "./button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Wrapper do react-day-picker v9 com estilos shadcn + locale PT-BR.
 * Os classNames seguem a nova API do v9 (day_button, weekdays, etc).
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      locale={ptBR}
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        // No v9 o <nav> (voltar/avançar) é IRMÃO dos meses, não filho do caption. O antigo
        // `nav: "contents"` + order-first/order-last derramava os botões na coluna dos meses —
        // o "avançar" ia parar DEPOIS da grade, embaixo de tudo (parecia sumido, principalmente
        // no celular). Aqui o nav é absoluto na linha do caption: ‹ Mês ›, como se espera.
        months: "relative flex flex-col sm:flex-row gap-2",
        month: "flex flex-col gap-3",
        month_caption: "flex h-9 items-center justify-center px-9",
        caption_label:
          "pointer-events-none inline-flex items-center gap-1 text-sm font-semibold capitalize",
        nav: "absolute inset-x-0 top-0 z-10 flex h-9 items-center justify-between",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 shrink-0 bg-transparent p-0 opacity-60 hover:opacity-100",
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 shrink-0 bg-transparent p-0 opacity-60 hover:opacity-100",
        ),
        dropdowns: "flex flex-1 items-center justify-center gap-1.5",
        dropdown_root:
          "relative inline-flex items-center rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs font-semibold text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 focus-within:border-brand-400 focus-within:ring-1 focus-within:ring-brand-400 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted",
        dropdown:
          "absolute inset-0 cursor-pointer appearance-none bg-transparent opacity-0",
        months_dropdown: "capitalize",
        years_dropdown: "tabular-nums",
        month_grid: "w-full border-collapse",
        weekdays: "flex w-full",
        weekday:
          "w-9 text-[10.5px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground",
        week: "flex w-full mt-1",
        day: "relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-10",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 rounded-full p-0 font-medium aria-selected:opacity-100",
        ),
        selected:
          "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
        today:
          "[&>button]:ring-2 [&>button]:ring-primary [&>button]:ring-offset-1 [&>button]:ring-offset-background [&>button]:font-bold",
        outside:
          "[&>button]:text-muted-foreground [&>button]:opacity-40",
        disabled: "[&>button]:text-muted-foreground [&>button]:opacity-40",
        range_start:
          "[&>button]:bg-primary [&>button]:text-primary-foreground",
        range_middle:
          "[&>button]:bg-accent [&>button]:text-accent-foreground [&>button]:rounded-none",
        range_end: "[&>button]:bg-primary [&>button]:text-primary-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === "left" ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
}
